import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useAuth } from '../../contexts/AuthContext';
import { useTeacher } from '../../hooks/useTeacher';
import { getTeacherWeekSchedule, TimetableEntry } from '../../services/timetableService';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;
const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

const parseTimeSlotToMinutes = (timeSlot: string): number => {
  try {
    const startPart = timeSlot.split('-')[0].trim();
    const match = startPart.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return 0;
    
    let [_, hoursStr, minutesStr, ampm] = match;
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    if (ampm.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return hours * 60 + minutes;
  } catch {
    return 0;
  }
};

const TeacherTimetablePage: React.FC = () => {
  const { user } = useAuth();
  const schoolId = user?.schoolId ?? undefined;

  const { useStaffByEmail } = useTeacher(schoolId);
  const staffQuery = useStaffByEmail(user?.email);
  const teacherId = staffQuery.data?.id;

  const [entries, setEntries] = useState<TimetableEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!teacherId) return;
    setLoading(true);
    getTeacherWeekSchedule(teacherId)
      .then((data) => setEntries(data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [teacherId]);

  // Collect unique time slot rows ordered chronologically by start time
  const timeSlotRows = useMemo(() => {
    const seen = new Map<string, { timeSlot: string; periodNumber: number; isBreak: boolean }>();
    entries.forEach((e) => {
      const key = e.isBreak ? `break-${e.periodNumber}` : `period-${e.periodNumber}`;
      if (!seen.has(key)) {
        seen.set(key, {
          timeSlot: e.timeSlot,
          periodNumber: e.periodNumber,
          isBreak: e.isBreak,
        });
      }
    });

    const allSlots = Array.from(seen.values());
    return allSlots.sort((a, b) => {
      const timeA = parseTimeSlotToMinutes(a.timeSlot);
      const timeB = parseTimeSlotToMinutes(b.timeSlot);
      return timeA - timeB;
    });
  }, [entries]);

  // Build lookup: rowKey -> day -> entry
  const cellMap = useMemo(() => {
    const map: Record<string, Record<string, TimetableEntry>> = {};
    entries.forEach((e) => {
      const rowKey = e.isBreak ? `break-${e.periodNumber}` : `period-${e.periodNumber}`;
      if (!map[rowKey]) map[rowKey] = {};
      map[rowKey][e.dayOfWeek] = e;
    });
    return map;
  }, [entries]);

  const isEmpty = !loading && entries.length === 0;
  const isStaffLoading = staffQuery.isLoading;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: '#EEF4FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CalendarMonthIcon sx={{ color: '#1976d2', fontSize: '1.3rem' }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b', lineHeight: 1.2 }}>
            My Timetable
          </Typography>
          {staffQuery.data && (
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
              {`${staffQuery.data.firstName ?? ''} ${staffQuery.data.lastName ?? ''}`.trim()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Loading ── */}
      {(loading || isStaffLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress size={36} sx={{ color: '#1976d2' }} />
        </Box>
      )}

      {/* ── Empty ── */}
      {!loading && !isStaffLoading && isEmpty && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 12,
            color: '#94a3b8',
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: 64, mb: 2, opacity: 0.35 }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#94a3b8' }}>
            No schedule assigned yet
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.5 }}>
            Your teaching schedule will appear here once the principal assigns it.
          </Typography>
        </Box>
      )}

      {/* ── Grid Table ── */}
      {!loading && !isStaffLoading && !isEmpty && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1.5px solid #e2e8f0',
          }}
        >
          <TableContainer>
            <Table size="small" sx={{ minWidth: 600, tableLayout: 'fixed' }}>
              {/* ── Header Row ── */}
              <TableHead>
                <TableRow>
                  {/* Time column */}
                  <TableCell
                    sx={{
                      width: 150,
                      minWidth: 150,
                      bgcolor: '#f1f5f9',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: '#475569',
                      borderRight: '1.5px solid #e2e8f0',
                      borderBottom: '1.5px solid #e2e8f0',
                      textAlign: 'center',
                      py: 1.5,
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      overflow: 'hidden',
                    }}
                  >
                    Time
                  </TableCell>
                  {DAYS.map((day) => (
                    <TableCell
                      key={day}
                      align="center"
                      sx={{
                        bgcolor: '#1976d2',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        borderRight: '1px solid rgba(255,255,255,0.18)',
                        borderBottom: '1.5px solid #1565c0',
                        py: 1.5,
                        letterSpacing: 0.3,
                      }}
                    >
                      {DAY_LABELS[day]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              {/* ── Data Rows ── */}
              <TableBody>
                {timeSlotRows.map((row) => {
                  const rowKey = row.isBreak ? `break-${row.periodNumber}` : `period-${row.periodNumber}`;
                  const rowCells = cellMap[rowKey] || {};

                  if (row.isBreak) {
                    const dayEntries = Object.values(rowCells);
                    const matchedEntry = dayEntries.find((e) => e && e.breakName);
                    const breakName = matchedEntry?.breakName || 'Lunch Break';

                    return (
                      <TableRow key={rowKey}>
                        <TableCell
                          sx={{
                            bgcolor: '#f1f5f9',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#475569',
                            borderRight: '1.5px solid #e2e8f0',
                            borderBottom: '1px solid #e2e8f0',
                            textAlign: 'center',
                            py: 1.25,
                            width: 150,
                            minWidth: 150,
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                          }}
                        >
                          {row.timeSlot}
                        </TableCell>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            py: 1.25,
                          }}
                        >
                          <Typography
                            sx={{
                              fontStyle: 'italic',
                              fontSize: '0.8rem',
                              color: '#64748b',
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            {`— ${breakName} —`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow
                      key={rowKey}
                      sx={{ '&:hover': { bgcolor: '#f8faff' }, transition: 'background 0.15s' }}
                    >
                      {/* Time column */}
                      <TableCell
                        sx={{
                          bgcolor: '#f1f5f9',
                          fontWeight: 700,
                          fontSize: '0.73rem',
                          color: '#475569',
                          borderRight: '1.5px solid #e2e8f0',
                          borderBottom: '1px solid #e2e8f0',
                          textAlign: 'center',
                          py: 1.5,
                          width: 150,
                          minWidth: 150,
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>
                          {`Period ${row.periodNumber}`}
                        </Typography>
                        {row.timeSlot}
                      </TableCell>

                      {/* Day columns */}
                      {DAYS.map((day) => {
                        const entry = rowCells[day];
                        return (
                          <TableCell
                            key={day}
                            align="center"
                            sx={{
                              borderRight: '1px solid #e2e8f0',
                              borderBottom: '1px solid #e2e8f0',
                              py: 1.5,
                              px: 1,
                              bgcolor: entry ? '#fff' : '#fafbff',
                            }}
                          >
                            {entry ? (
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {entry.subjectName || '—'}
                                </Typography>
                                {entry.className && (
                                  <Typography
                                    sx={{
                                      fontSize: '0.7rem',
                                      color: '#1976d2',
                                      fontWeight: 500,
                                      lineHeight: 1.3,
                                      mt: 0.25,
                                    }}
                                  >
                                    {entry.className}
                                    {entry.section ? ` (${entry.section})` : ''}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: '0.75rem', color: '#e2e8f0' }}>—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default TeacherTimetablePage;
